import { creatorbase, creatorbaseQueries } from '@round/api';
import { DropdownOptionWithSubLabel, SelectProps } from '@round/ui-kit';
import useSelectProps from 'Hooks/useSelectProps';
import { useInfiniteQuery } from '@tanstack/react-query';
import { isNumber } from 'utility/utility';
import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import { useBrand, useTeamsQueriesByIds } from '@round/creatorbase-helpers/api-hooks';

const queryOptions = creatorbaseQueries.brand.options.infinite;

export type BrandOption = DropdownOptionWithSubLabel<number> & {
    image?: string | null;
};

export const mapBrandToOption = (brand: creatorbase.Brand, team?: creatorbase.Team | null): BrandOption => ({
    value: brand.id,
    label: brand.name,
    subLabel: team?.name || '',
    image: brand.image,
});

type Params = {
    initOn: 'menuOpen' | 'mount';
    initialValueId?: number;
    params?: Omit<creatorbase.GetBrandsParams, 'page_size' | 'search'>;
    enabled?: boolean;
};

type Return = {
    getProps: (override?: Pick<SelectProps<BrandOption>, 'onChange'>) => SelectProps<BrandOption>;
};

export default function useBrandSelect({ enabled = true, ...params }: Params = { initOn: 'mount' }): Return {
    const { data: brand } = useBrand(params.initialValueId, { enabled: isNumber(params.initialValueId) });
    const initialValue = useMemo(() => (brand ? mapBrandToOption(brand) : null), [brand]);

    const { getProps, isMenuOpen, debouncedSearchValue } = useSelectProps<BrandOption, false>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage, isError } = useInfiniteQuery({
        ...queryOptions({ ...params.params, page_size: 25, search: debouncedSearchValue }),
        enabled: enabled && ((params.initOn === 'menuOpen' && isMenuOpen) || params.initOn === 'mount'),
    });

    const teamIds = data?.pages.map((page) => uniq(page.results.map((b) => b.team_id))) ?? [];
    const teamQueries = useTeamsQueriesByIds(teamIds);

    const options: BrandOption[] =
        data?.pages.flatMap((page, pageIndex) => {
            const teamQuery = teamQueries[pageIndex];

            return page.results.map((b) => {
                const team = teamQuery?.data?.results.find((t) => t.id === b.team_id);
                return mapBrandToOption(b, team);
            });
        }) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                noOptionsMessage: () => (isError ? 'Could not get brands' : 'No brands found'),
                isLoading: isFetching || teamQueries.some((r) => r.isFetching),
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        void fetchNextPage();
                    }
                },
                filterOption: null,
            }),
    };
}
